import { useCallback, useEffect, useRef, useState } from 'react';

interface UseCountdownOptions {
  /** Start counting down as soon as the hook mounts. Defaults to false. */
  autoStart?: boolean;
  /** Called once when the countdown reaches zero (not when stopped early). */
  onComplete?: () => void;
}

/**
 * A second-resolution countdown from `seconds` down to zero.
 *
 * The remaining time is derived from a deadline timestamp rather than
 * decremented on each tick, so a throttled background tab still reports the
 * right number of seconds once it wakes up. State lives in memory only: a
 * remount restarts (or clears) the countdown.
 *
 * `start()` (re)starts from the full duration; `stop()` clears it to zero
 * without firing `onComplete`.
 */
export function useCountdown(
  seconds: number,
  { autoStart = false, onComplete }: UseCountdownOptions = {},
) {
  const [deadline, setDeadline] = useState<number | null>(() =>
    autoStart ? Date.now() + seconds * 1000 : null,
  );
  const [now, setNow] = useState(() => Date.now());

  const secondsLeft =
    deadline === null ? 0 : Math.max(0, Math.ceil((deadline - now) / 1000));
  const isRunning = secondsLeft > 0;

  // Sync immediately when the interval is armed: effects can run well after
  // the render that started the countdown, and without this the displayed
  // time would stay stale until the first tick.
  useEffect(() => {
    if (!isRunning) return;
    const tick = () => setNow(Date.now());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isRunning]);

  // Keep the latest callback without making it an effect dependency, so an
  // inline arrow from the caller doesn't re-arm the completion effect.
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const wasRunning = useRef(isRunning);
  useEffect(() => {
    const reachedZero = wasRunning.current && !isRunning && deadline !== null;
    wasRunning.current = isRunning;
    if (reachedZero) onCompleteRef.current?.();
  }, [isRunning, deadline]);

  const start = useCallback(() => {
    const at = Date.now();
    setNow(at);
    setDeadline(at + seconds * 1000);
  }, [seconds]);

  const stop = useCallback(() => setDeadline(null), []);

  return { secondsLeft, isRunning, start, stop };
}
